import { getCurrentSession, supabase } from './supabase';
import { browserTimeZone, daysAgoIn, formatInstant, formatTimeOnly, todayIn } from './lib/time';

/**
 * Where the API lives. Set REACT_APP_API_BASE at build time to override: use
 * http://localhost:8000 against a locally running backend, or '' when the API is
 * served from the same origin as this bundle (the single-container demo). Unset,
 * it is production — a build that forgets the variable must not ship pointing at
 * localhost.
 */
const envApiBase = process.env.REACT_APP_API_BASE;
export const API_BASE = (
  envApiBase === undefined ? 'https://api.callvibe.ai' : envApiBase
).replace(/\/+$/, '');

/**
 * @deprecated Use `todayIn(timeZone)` from ./lib/time with the tenant's zone from
 * usePreferences(). These wrappers now resolve against the BROWSER's zone rather
 * than UTC — still a guess, but never the wrong calendar day for the person
 * looking at the screen, which `toISOString()` was for most of the world for part
 * of every day.
 */
export const getTodayString = () => todayIn(browserTimeZone());

/** @deprecated Use `daysAgoIn(timeZone, days)` from ./lib/time. */
export const getDaysAgo = (days) => daysAgoIn(browserTimeZone(), days);

export const getWeekStart = () => {
  const date = new Date();
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);
  date.setDate(diff);
  return date.toISOString().split('T')[0];
};

export const getMonthStart = () => {
  const date = new Date();
  date.setDate(1);
  return date.toISOString().split('T')[0];
};

export const getDateRangeLabel = (filters) => {
  if (!filters || filters.dateRange === 'all') return 'All Time';
  switch (filters.dateRange) {
    case 'today': return 'Today';
    case 'yesterday': return 'Yesterday';
    case 'last7days': return 'Last 7 Days';
    case 'last30days': return 'Last 30 Days';
    case 'thisweek': return 'This Week';
    case 'thismonth': return 'This Month';
    case 'custom':
      return (filters.startDate && filters.endDate)
        ? `${filters.startDate} to ${filters.endDate}` : 'Custom Range';
    default: return filters.dateRange;
  }
};

export const isAuthenticated = async () => {
  try {
    const { data: { session } } = await getCurrentSession();
    return !!session;
  } catch (error) {
    console.error('Error checking authentication:', error);
    return false;
  }
};

export const getAuthToken = async () => {
  try {
    const { data: { session } } = await getCurrentSession();
    return session?.access_token || null;
  } catch (error) {
    console.error('Error getting auth token:', error);
    return null;
  }
};

export const getAuthHeaders = async () => {
  const token = await getAuthToken();
  return token ? {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  } : {
    'Content-Type': 'application/json'
  };
};

export const authenticatedFetch = async (url, options = {}) => {
  const token = await getAuthToken();

  // If no token and not a public endpoint, return a fake 401 response
  // to avoid unnecessary network requests
  if (!token) {
    return new Response(JSON.stringify({ error: 'Not authenticated' }), {
      status: 401,
      headers: { 'Content-Type': 'application/json' }
    });
  }

  let headers = {
    'Authorization': `Bearer ${token}`,
    'X-Tenant-Hostname': window.location.hostname,
    ...options.headers
  };

  if (!(options.body instanceof FormData)) {
    headers['Content-Type'] = headers['Content-Type'] || 'application/json';
  }

  const fullUrl = url.startsWith('http') ? url : `${API_BASE}${url}`;

  let response = await fetch(fullUrl, {
    ...options,
    headers
  });

  // One retry on 401: refresh token, then retry once
  if (response.status === 401) {
    try {
      // Only attempt refresh if there's an existing session to refresh
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      if (!currentSession) {
        // No session at all — force sign out to reset UI state
        await supabase.auth.signOut({ scope: 'local' });
        return response;
      }

      const { data: { session }, error } = await supabase.auth.refreshSession();

      if (session && !error) {
        headers = {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
          ...options.headers
        };

        response = await fetch(fullUrl, {
          ...options,
          headers
        });

        // If still 401 after refresh, session is truly invalid
        if (response.status === 401) {
          await supabase.auth.signOut({ scope: 'local' });
        }
      } else {
        // Refresh failed — session is expired, sign out
        await supabase.auth.signOut({ scope: 'local' });
      }
    } catch (refreshError) {
      // Refresh failed — session is truly expired, sign out
      await supabase.auth.signOut({ scope: 'local' });
    }
  }

  return response;
};

/**
 * @deprecated Use `formatInstant(iso, timeZone)` from ./lib/time.
 *
 * The name was a lie: these rendered in the BROWSER's timezone, not IST and not
 * the tenant's. So a UK manager reviewing an Indian team's calls saw UK clock
 * times sitting next to IST-bucketed analytics, and neither was labelled. They
 * now accept an explicit zone and default to the browser's, which is at least
 * what the old behaviour actually was.
 */
export const formatISTDateTime = (isoString, { includeYear = true, hour12 = true, tz } = {}) =>
  formatInstant(isoString, tz || browserTimeZone(), { withDate: true, withYear: includeYear, hour12 });

/** @deprecated Use `formatTimeOnly(iso, timeZone)` from ./lib/time. */
export const formatISTTime = (isoString, { hour12 = true, tz } = {}) =>
  formatTimeOnly(isoString, tz || browserTimeZone(), { hour12 });

// Given a UTC hour (0-23), return a local hour range label like "09:00 AM - 10:00 AM"
export const formatISTHourRangeFromUTCHour = (utcHour) => {
  try {
    const base = new Date(Date.UTC(2000, 0, 1, utcHour, 0, 0));
    const end = new Date(Date.UTC(2000, 0, 1, (utcHour + 1) % 24, 0, 0));
    const fmt = (d) => d.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
    return `${fmt(base)} - ${fmt(end)}`;
  } catch {
    return '';
  }
};

export const getSentimentEmoji = (sentiment) => {
  switch (sentiment?.toLowerCase()) {
    case 'positive': return '😊';
    case 'negative': return '😞';
    case 'neutral': return '😐';
    default: return '➖';
  }
};

/**
 * Sentiment chip classes, in design tokens.
 *
 * NEUTRAL IS GREY, NOT AMBER. A neutral call is not a warning, and amber was
 * doing double duty as the trial badge, the usage warning and the Action Items
 * nav item — so the one hue that should mean "needs attention" meant nothing.
 * Freeing it is what lets an SLA breach read as urgent.
 *
 * @deprecated Prefer <SentimentBadge> from ./components/ui/Badge, which also
 * pairs a glyph with the colour so red/green deficiency is not excluded.
 */
export const getSentimentColor = (sentiment) => {
  switch (sentiment?.toLowerCase()) {
    case 'positive': return 'text-good-text bg-good-tint';
    case 'negative': return 'text-bad-text bg-bad-tint';
    case 'neutral': return 'text-ink-700 bg-surface-2';
    default: return 'text-ink-500 bg-surface-2';
  }
};

export const getCallDirection = (inbound) => {
  if (inbound === null || inbound === undefined || inbound === '') {
    return 'Unknown';
  }

  const isInbound = inbound === true || inbound === 'true' || inbound === 1 || inbound === '1' || inbound === 'True';
  return isInbound ? 'Inbound' : 'Outbound';
};

export const getCallDirectionIcon = (inbound) => {
  if (inbound === null || inbound === undefined || inbound === '') {
    return 'fas fa-phone';
  }

  const isInbound = inbound === true || inbound === 'true' || inbound === 1 || inbound === '1' || inbound === 'True';
  return isInbound ? 'fas fa-phone-alt' : 'fas fa-phone';
};

export const getCallStatus = (answered, inbound) => {
  if (answered === null || answered === undefined || answered === '') {
    return 'Unknown';
  }

  const isAnswered = answered === true || answered === 'true' || answered === 1 || answered === '1' || answered === 'True';
  const isInbound = inbound === true || inbound === 'true' || inbound === 1 || inbound === '1' || inbound === 'True';
  const notAnsweredLabel = isInbound ? 'Missed' : 'No Reply';
  return isAnswered ? 'Answered' : notAnsweredLabel;
};

export const getCallStatusIcon = (answered, inbound) => {
  if (answered === null || answered === undefined || answered === '') {
    return 'fas fa-question-circle';
  }

  const isAnswered = answered === true || answered === 'true' || answered === 1 || answered === '1' || answered === 'True';
  const isInbound = inbound === true || inbound === 'true' || inbound === 1 || inbound === '1' || inbound === 'True';
  const notAnsweredIcon = isInbound ? 'fas fa-times-circle' : 'fas fa-comment-slash';
  return isAnswered ? 'fas fa-check-circle' : notAnsweredIcon;
};

export const getCallStatusColor = (answered, inbound) => {
  if (answered === null || answered === undefined || answered === '') {
    return 'text-ink-500 bg-surface-2';
  }

  const isAnswered = answered === true || answered === 'true' || answered === 1 || answered === '1' || answered === 'True';
  const isInbound = inbound === true || inbound === 'true' || inbound === 1 || inbound === '1' || inbound === 'True';
  // An unanswered OUTBOUND call is not a failure worth alarming about — nobody
  // picked up, which is normal. An unanswered inbound call is lost revenue.
  const notAnsweredColor = isInbound ? 'text-bad-text bg-bad-tint' : 'text-ink-700 bg-surface-2';
  return isAnswered ? 'text-good-text bg-good-tint' : notAnsweredColor;
};

export const formatDuration = (duration) => {
  if (!duration || duration === 0 || duration === '0') {
    return '0:00';
  }

  let seconds = parseInt(duration);

  if (isNaN(seconds)) {
    if (typeof duration === 'string' && duration.includes(':')) {
      const parts = duration.split(':');
      if (parts.length === 2) {
        const minutes = parseInt(parts[0]) || 0;
        const secs = parseInt(parts[1]) || 0;
        seconds = minutes * 60 + secs;
      } else {
        return duration;
      }
    } else {
      return '-';
    }
  }

  seconds = Math.max(0, seconds);

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}:${remainingMinutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  }

  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

export const validateCallData = (data) => {
  if (!Array.isArray(data)) {
    console.warn('Call data is not an array:', data);
    return [];
  }

  return data.map((call, index) => {
    const validated = {
      ...call,
      answered: call.answered !== null && call.answered !== undefined ?
        (call.answered === true || call.answered === 'true' || call.answered === 1 || call.answered === '1' || call.answered === 'True') : null,

      inbound: call.inbound !== null && call.inbound !== undefined ?
        (call.inbound === true || call.inbound === 'true' || call.inbound === 1 || call.inbound === '1' || call.inbound === 'True') : null,

      duration: call.duration || 0,
      phonebookName: call.phonebookName || '',
      userName: call.userName || '',
      sourceDetail: call.sourceDetail || '',
      sentiment: call.sentiment || '',
      category: call.category || '',
      startTime: call.startTime || '',
      transcription: call.transcription || '',
      summary: call.summary || '',
      call_to_action: call.call_to_action || '',
      recorded: (!!(call.recUrl && call.recUrl.trim() !== '') || !!(call.gcs_audio_path && call.gcs_audio_path.trim() !== ''))
    };

    return validated;
  });
};

export const getRecordingStatus = (recorded) => {
  if (recorded === null || recorded === undefined) {
    return 'Unknown';
  }
  return recorded ? 'Recorded' : 'Not Recorded';
};

export const getRecordingStatusIcon = (recorded) => {
  if (recorded === null || recorded === undefined) {
    return 'fas fa-question-circle';
  }
  return recorded ? 'fas fa-microphone' : 'fas fa-microphone-slash';
};

export const getRecordingStatusColor = (recorded) => {
  if (recorded === null || recorded === undefined) {
    return 'text-ink-500 bg-surface-2';
  }
  // A missing recording is a gap in what we can analyse, not an error state.
  return recorded ? 'text-good-text bg-good-tint' : 'text-ink-500 bg-surface-2';
};

